import { Box, Button, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { object, string } from 'yup';
import {
  BeginAdminMfaEnrollmentDocument,
  BeginAdminMfaEnrollmentMutation,
  BeginAdminMfaEnrollmentMutationVariables,
  ConfirmAdminMfaEnrollmentDocument,
  ConfirmAdminMfaEnrollmentMutation,
  ConfirmAdminMfaEnrollmentMutationVariables,
} from '../../../generated/graphql';
import useSilentMutation from '../../../graphql/hooks/useSilentMutation';
import { useAuth } from '../../../providers/auth.provider';
import { TextField } from '../../../ui';

const confirmValidation = object({
  code: string()
    .required('Enter the 6-digit code')
    .matches(/^\d{6}$/, 'Enter the 6-digit code'),
});

export const MfaEnrollment = () => {
  const { mfaGate, setBackupCodes } = useAuth();
  const [beginEnrollment, { data, loading: beginLoading }] = useSilentMutation<
    BeginAdminMfaEnrollmentMutation,
    BeginAdminMfaEnrollmentMutationVariables
  >(BeginAdminMfaEnrollmentDocument);
  const [confirmEnrollment, { loading: confirmLoading }] = useSilentMutation<
    ConfirmAdminMfaEnrollmentMutation,
    ConfirmAdminMfaEnrollmentMutationVariables
  >(ConfirmAdminMfaEnrollmentDocument);

  useEffect(() => {
    beginEnrollment().catch(() => undefined);
  }, [beginEnrollment]);

  const formik = useFormik({
    initialValues: { code: '' },
    validationSchema: confirmValidation,
    onSubmit: async ({ code }) => {
      const result = await confirmEnrollment({ variables: { code } });
      const backupCodes = result.data?.confirmAdminMfaEnrollment.backupCodes;
      if (backupCodes) {
        setBackupCodes(backupCodes);
      }
    },
  });

  const enrollment = data?.beginAdminMfaEnrollment;

  return (
    <Box
      component="form"
      onSubmit={formik.handleSubmit}
      display="grid"
      rowGap={2.5}
    >
      <Typography variant="subtitle1">Set up authenticator app</Typography>
      <Typography variant="body2">
        Scan the QR code with Google Authenticator, 1Password, or any TOTP app,
        then enter the current 6-digit code. You cannot skip this step.
      </Typography>
      {mfaGate?.isServiceAccount ? (
        <Typography variant="body2">
          Save the QR code or otpauth URI and the backup codes in the shared
          1Password vault. Teammates must import that item rather than scanning
          a new QR — enrolling again rotates the secret and locks everyone else
          out.
        </Typography>
      ) : null}
      {enrollment ? (
        <>
          <Box
            component="img"
            src={enrollment.qrCodeDataUrl}
            alt="Authenticator QR code"
            sx={{ width: 200, height: 200, justifySelf: 'start' }}
          />
          <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
            Manual secret: {enrollment.secret}
          </Typography>
        </>
      ) : null}
      <TextField
        id="code"
        name="code"
        label="Authentication code"
        value={formik.values.code}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.code && Boolean(formik.errors.code)}
        helperText={formik.touched.code && formik.errors.code}
        inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
        sx={{ borderRadius: '8px' }}
      />
      <Button
        variant="contained"
        disabled={
          formik.isSubmitting || confirmLoading || beginLoading || !enrollment
        }
        type="submit"
        sx={{ minWidth: 160, justifySelf: 'end' }}
      >
        Confirm
      </Button>
    </Box>
  );
};
